import { formatDate } from 'utils/dates'
import { formatCurrency, formatNumber, formatRate } from 'utils/numbers'
import { formatInvoiceStatusText } from 'utils/status'
import { ColorProps } from 'utils/types'

import PartnerLogo from '@/ui/PartnerLogo'

import Footer from './Footer'
import * as css from './styles'
import { ILineItems, IInvoice, IPartner } from './types'

// qty=0 with no unit price = integration shipping/freight line where the
// charge lives in extendedAmountPerItem (FIRE-1465). qty=0 with a unit
// price is a header/spec line whose canonical amount is 0 (FIRE-1678).
const lineItemAmount = (li: ILineItems): number =>
  li.quantity === 0 && !li.unitPrice
    ? li.extendedAmountPerItem ?? 0
    : (li.quantity ?? 0) * (li.unitPrice ?? 0)

const InvoicePdfLayout = ({
  partner,
  invoice,
  brandColor
}: {
  partner: IPartner
  invoice: IInvoice
  brandColor?: ColorProps
}) => {
  let hasLines = invoice?.lineItems?.length > 0
  let lineItems = []

  if (hasLines) {
    lineItems = [...invoice.lineItems]
  }
  if (!hasLines && invoice?.productName) {
    hasLines = true
    lineItems.push({
      description: invoice.productName,
      quantity: 1,
      extendedAmountPerItem: invoice.amount,
      unitPrice: invoice.amount
    })
  }

  // Helper function to calculate decimal places for a number
  const getDecimalPlaces = (num: number): number => {
    if (num == null || num % 1 === 0) return 2

    const str = num.toString()
    const decimalIndex = str.indexOf('.')

    if (decimalIndex === -1) return 2

    const decimalLength = str.substring(decimalIndex + 1).length
    return Math.min(5, Math.max(2, decimalLength))
  }

  let hasCategories = false
  let subtotal = 0
  invoice?.lineItems?.forEach(li => {
    subtotal += lineItemAmount(li)
    if (li.category) {
      hasCategories = true
    }
  })

  let discountAmountPercent = 0
  if (subtotal) {
    discountAmountPercent = (invoice?.totalDiscountAmount * 100) / subtotal
  }

  return (
    <table css={css.pageTable({ brandColor })}>
      <thead>
        <tr>
          <th>
            <div></div>
          </th>
        </tr>
      </thead>
      <tbody>
        <td>
          <div css={css.headerWrapper}>
            <div className='invoice-data'>
              <h1>Invoice #{invoice?.invoiceNumber}</h1>
              <div>
                <span>Bill</span>
                <span>{invoice?.customer?.name}</span>
              </div>
              <div>
                <span>Created on</span>
                <span>{formatDate(invoice?.createdAt, 'M/D/YY', 'utc')}</span>
              </div>
              <div>
                <span>Status</span>
                <span>
                  {invoice?.status && formatInvoiceStatusText(invoice.status)}
                </span>
              </div>
              <div>
                <span>Due on</span>
                <span>{formatDate(invoice?.dueDate, 'M/D/YY', 'utc')}</span>
              </div>
              {invoice?.purchaseOrder && (
                <div>
                  <span>Purchase Order</span>
                  <span>{invoice?.purchaseOrder}</span>
                </div>
              )}
            </div>

            <div className='partner-data'>
              <div className='logo-wrapper'>
                <PartnerLogo height={3} />
              </div>
              <div>
                <span className='partner-name'>{partner?.name}</span>
                <span>{partner?.streetAddress}</span>
                <span>{partner?.subPremise}</span>
                <span>
                  {partner?.city}, {partner?.state} {''} {partner?.postalCode}
                </span>
              </div>
            </div>
          </div>

          <div css={css.detailsWrapper({ hasLines })}>
            <h2>Invoice details</h2>

            <table className='lines'>
              <thead>
                <tr>
                  {hasCategories && <th>Category</th>}
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {!hasLines && (
                  <tr>
                    {hasCategories && <td>-</td>}
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}
                {lineItems?.map((li, i) => (
                  <tr key={`line-item-${i}`}>
                    {hasCategories && <td>{li.category || '-'}</td>}
                    <td>
                      <pre>{li.description}</pre>
                    </td>
                    <td>{formatNumber({ amount: li.quantity, places: 2 })}</td>
                    <td>
                      {formatCurrency({
                        amount: li.unitPrice,
                        places: getDecimalPlaces(li.unitPrice)
                      })}
                    </td>
                    <td>{formatCurrency({ amount: lineItemAmount(li) })}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {hasLines ? (
              <table css={css.resumeTableWithLines}>
                <tbody>
                  <tr>
                    <td>
                      <div className='resume'>
                        <div>
                          <span>Subtotal</span>
                          <span>{formatCurrency({ amount: subtotal })}</span>
                        </div>
                        {invoice?.totalTaxAmount > 0 && (
                          <div>
                            <span>Sales tax</span>
                            <span>
                              {formatCurrency({
                                amount: invoice.totalTaxAmount
                              })}
                            </span>
                          </div>
                        )}
                        {invoice?.totalDiscountAmount > 0 && (
                          <div>
                            <span>
                              Discount{' '}
                              {formatRate({
                                amount: discountAmountPercent,
                                places: 2
                              })}
                              %
                            </span>
                            <span>
                              {formatCurrency({
                                amount: invoice.totalDiscountAmount
                              })}
                            </span>
                          </div>
                        )}
                        <div>
                          <span>Total due</span>
                          <span>
                            {formatCurrency({ amount: invoice?.amount })}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <table css={css.resumeTableWithoutLines}>
                <tbody>
                  <tr>
                    <td>
                      <div className='resume'>
                        <span>Total due</span>
                        <span>
                          {formatCurrency({ amount: invoice?.amount })}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </td>
      </tbody>
      <tfoot>
        <tr>
          <td>
            <Footer />
          </td>
        </tr>
      </tfoot>
    </table>
  )
}

export default InvoicePdfLayout
